import React, { useState } from 'react';
import { motion } from 'framer-motion';
import PillBadge from './PillBadge';

const TestimonialsSection = ({ userData }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  const allTestimonials = [
    {
      quote: `Collaborating with Prabhakar and his team brought fresh creative energy and technical excellence to our project.`,
      author: "Chanchal Sanyal",
      role: "Founder at QED Communications",
      avatar: "/Testimonials/01 Chanchal Sanyal.jpg"
    },
    {
      quote: `Prabhakar and his team managed our project smoothly, delivering outstanding visuals and seamless edits that truly impressed us.`,
      author: "Ashutosh Ahluwalia",
      role: "Head-Business Development & Customer Experience",
      avatar: "/Testimonials/02 Ashutosh Ahluwalia.jpeg"
    },
    {
      quote: `Working with Prabhakar and his talented team was a fantastic experience. Their expertise in editing and motion graphics brought our vision to life perfectly.`,
      author: "Amber Kumar",
      role: "Founder at Drutgami Production",
      avatar: "/Testimonials/03 Amber Kumar.jpeg"
    },
    {
      quote: `Prabhakar's video editing skills stood out—the project was smooth and professional from start to finish, truly a joy to work with.`,
      author: "Gareth Thomos",
      role: "Business Head - Z Live",
      avatar: "/Testimonials/04 Gareth.jpeg"
    },
    {
      quote: "Working together was a pleasure—the project was managed with skill, and every challenge became a new creative opportunity.",
      author: "Suraj Wadhwani",
      role: "Financial Services Representative | CIBC",
      avatar: "/Testimonials/05 Suraj Wadhwani.jpeg"
    },
    {
      quote: "From planning to delivery, every detail was handled so well that the entire production felt effortless and enjoyable—the final video exceeded our expectations.",
      author: "Swaroop Banarji",
      role: "Former COO Zee Live, Founder Chaos Australia",
      avatar: "/Testimonials/06 Swaroop.jpg"
    },
    {
      quote: "Outstanding video editing that turned our footage into a captivating story with perfect rhythm and flow.",
      author: "Abhishek Mehata",
      role: "Entrepreneur & Event Producer",
      avatar: "/Testimonials/07 Abhishek Mehata.jpeg"
    },
    {
      quote: "Motion graphics enhancements gave our videos a professional and polished look.",
      author: "Dheeraj Sethia",
      role: "Co-Founder, Fillme Networks",
      avatar: "/Testimonials/08 Dheeratj Setia.png"
    },
    {
      quote: "Working with him, the result is an electrifying consumer experience.",
      author: "Ashutosh Jha",
      role: "Sr. Director, Grapes Digital",
      avatar: "/Testimonials/09 Ashutosh Jha.jpeg"
    },
    {
      quote: "The team's attention to detail and creative vision made our project stand out in the market.",
      author: "Deepak Rao",
      role: "Content Director, Zero Studio",
      avatar: "/Testimonials/10 Deepak Rao.jpeg"
    },
    {
      quote: "Professional, creative, and always delivers beyond expectations.",
      author: "Aman Saini",
      role: "Executive Producer",
      avatar: "/Testimonials/11 Aman Saini.jpeg"
    },
    {
      quote: "His expertise in video production and post-production is unmatched in the industry.",
      author: "Harihar Goswami",
      role: "Creative Director at QED Communications",
      avatar: "/Testimonials/12 harihar goswami.jpeg"
    },
    {
      quote: "From concept to final delivery, the entire process was seamless and professional.",
      author: "Abhishek Vijay Makhija",
      role: "Filmmaker | Independent Media Professional",
      avatar: "/Testimonials/13 Abhishek Vijay Makhija.png"
    },
    {
      quote: "Working with him has been a game-changer for our brand's visual storytelling.",
      author: "Muni Agarwal",
      role: "AI | Advertising Films Director",
      avatar: "/Testimonials/14 Muni Agrwal.jpeg"
    }
  ];

  // Divide testimonials into three batches
  const batch1 = allTestimonials.slice(0, 5); // First 5 testimonials
  const batch2 = allTestimonials.slice(5, 9); // Next 4 testimonials  
  const batch3 = allTestimonials.slice(9, 14); // Last 5 testimonials

  // Carousel navigation functions
  const goToNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % allTestimonials.length);
  };

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + allTestimonials.length) % allTestimonials.length);
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  return (
    <section id="testimonials" className="py-12 sm:py-20 bg-black">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-8 sm:mb-16"
        >
          <PillBadge text={"Testimonials"} icon={''} className="mb-4 sm:mb-8" />
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl leading-tight tracking-tight mb-6"
          >
            <span className="text-[#999999]">See what </span>
            <span className="text-white">others</span>
            <span className="text-[#999999]"> say about me</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
            className="text-[#999999] text-lg leading-relaxed max-w-2xl mx-auto"
          >
            I have helped many businesses make a killer design for their product. Wanna be the next?
          </motion.p>
        </motion.div>

        {/* Mobile Carousel - Show only on mobile */}
        <div className="lg:hidden relative">
          <div className="relative overflow-hidden">
            <motion.div
              className="flex cursor-grab active:cursor-grabbing"
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.3}
              onDragEnd={(e, { offset, velocity }) => {
                const swipeThreshold = 50;
                const velocityThreshold = 500;

                // Determine swipe direction and strength
                if (Math.abs(offset.x) > swipeThreshold || Math.abs(velocity.x) > velocityThreshold) {
                  if (offset.x > 0) {
                    // Swiped right - go to previous
                    goToPrevious();
                  } else {
                    // Swiped left - go to next
                    goToNext();
                  }
                }
                // If swipe is not strong enough, it will animate back to currentIndex
              }}
              animate={{ x: `-${currentIndex * 100}%` }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            >
              {allTestimonials.map((testimonial, index) => (
                <div
                  key={index}
                  className="w-full flex-shrink-0 px-2"
                >
                  <div className="bg-[#0E0E0E] border border-[#333333] rounded-2xl p-6 hover:bg-[#0f0f0f] transition-colors duration-300">
                    <p className="text-[#b3b3b3] leading-relaxed mb-4 text-left text-sm">
                      "{testimonial.quote}"
                    </p>
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-full border border-white/20 overflow-hidden bg-[#1a1a1a]">
                        <img 
                          src={testimonial.avatar} 
                          alt={testimonial.author}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div>
                        <h4 className="text-white font-semibold text-left text-sm">{testimonial.author}</h4>
                        {testimonial.role ? (
                          <p className="text-[#999999] text-xs text-left">{testimonial.role}</p>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Dots Indicator */}
          <div className="flex justify-center mt-6 gap-2">
            {allTestimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`h-2 rounded-full transition-all ${
                  index === currentIndex ? 'w-8 bg-white' : 'w-2 bg-white/30'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Desktop Grid - 3 columns on desktop */}
        <div className="hidden lg:grid lg:grid-cols-3 gap-8">
          {/* Column 1 - Moving Up */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            viewport={{ once: true }}
            className="relative h-[600px] overflow-hidden"
          >
            <motion.div
              className="space-y-6"
              animate={{ y: [0, -1 * (batch1.length * 200)] }}
              transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
            >
              {[...batch1, ...batch1].map((testimonial, index) => (
                <div
                  key={index}
                  className="bg-[#0E0E0E] border border-[#333333] rounded-2xl p-6 hover:bg-[#0f0f0f] transition-colors duration-300"
                >
                  <p className="text-[#b3b3b3] leading-relaxed mb-4 text-left text-sm">
                    "{testimonial.quote}"
                  </p>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-full border border-white/20 overflow-hidden bg-[#1a1a1a]">
                      <img 
                        src={testimonial.avatar} 
                        alt={testimonial.author}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <h4 className="text-white font-semibold text-left text-sm">{testimonial.author}</h4>
                      {testimonial.role ? (
                        <p className="text-[#999999] text-xs text-left">{testimonial.role}</p>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}
            </motion.div>
            {/* Fade overlays */}
            <div className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-black to-transparent" />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black to-transparent" />
          </motion.div>

          {/* Column 2 - Moving Down (Desktop only) */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="relative h-[600px] overflow-hidden hidden lg:block"
          >
            <motion.div
              className="space-y-6"
              animate={{ y: [-1 * (batch2.length * 200), 0] }}
              transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
            >
              {[...batch2, ...batch2].map((testimonial, index) => (
                <div
                  key={index}
                  className="bg-[#0E0E0E] border border-[#333333] rounded-2xl p-6 hover:bg-[#0f0f0f] transition-colors duration-300"
                >
                  <p className="text-[#b3b3b3] leading-relaxed mb-4 text-left text-sm">
                    "{testimonial.quote}"
                  </p>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-full border border-white/20 overflow-hidden bg-[#1a1a1a]">
                      <img 
                        src={testimonial.avatar} 
                        alt={testimonial.author}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <h4 className="text-white font-semibold text-left text-sm">{testimonial.author}</h4>
                      {testimonial.role ? (
                        <p className="text-[#999999] text-xs text-left">{testimonial.role}</p>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}
            </motion.div>
            {/* Fade overlays */}
            <div className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-black to-transparent" />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black to-transparent" />
          </motion.div>

          {/* Column 3 - Moving Up (Desktop only) */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
            className="relative h-[600px] overflow-hidden hidden lg:block"
          >
            <motion.div
              className="space-y-6"
              animate={{ y: [0, -1 * (batch3.length * 200)] }}
              transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
            >
              {[...batch3, ...batch3].map((testimonial, index) => (
                <div
                  key={index}
                  className="bg-[#0E0E0E] border border-[#333333] rounded-2xl p-6 hover:bg-[#0f0f0f] transition-colors duration-300"
                >
                  <p className="text-[#b3b3b3] leading-relaxed mb-4 text-left text-sm">
                    "{testimonial.quote}"
                  </p>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-full border border-white/20 overflow-hidden bg-[#1a1a1a]">
                      <img 
                        src={testimonial.avatar} 
                        alt={testimonial.author}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <h4 className="text-white font-semibold text-left text-sm">{testimonial.author}</h4>
                      {testimonial.role ? (
                        <p className="text-[#999999] text-xs text-left">{testimonial.role}</p>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}
            </motion.div>
            {/* Fade overlays */}
            <div className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-black to-transparent" />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black to-transparent" />
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;

